import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, Trash2, ShoppingBag, HeartOff } from "lucide-react";
import api, { cartUtils, inr, wishlistUtils } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export default function WishlistPage() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login?next=/wishlist"); return; }
    api.get("/wishlist").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [user, loading, nav]);

  const remove = async (pid) => {
    try {
      await wishlistUtils.remove(pid);
      setItems((cur) => (cur || []).filter((p) => p.id !== pid));
      toast.success("Removed from wishlist");
    } catch { toast.error("Could not remove"); }
  };

  const moveToCart = (p) => {
    if ((p.stock || 0) <= 0) { toast.error("This piece is currently out of stock."); return; }
    cartUtils.add(p, 1);
    toast.success(`Added ${p.name} to bag`);
  };

  if (loading || items === null) {
    return <div className="max-w-7xl mx-auto px-6 md:px-12 py-32 text-stone-500">Loading your wishlist…</div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
      <div className="flex items-center gap-3">
        <Heart size={26} className="text-[#B23A3A]" strokeWidth={1.5} fill="#B23A3A" />
        <p className="eyebrow">Your Wishlist</p>
      </div>
      <h1 className="font-serif text-4xl md:text-5xl mt-2 tracking-tight">Pieces you love</h1>
      <p className="mt-3 text-stone-600 max-w-2xl">
        Everything you've saved, ready to move to your bag whenever you're ready.
      </p>

      {items.length === 0 ? (
        <div data-testid="wishlist-empty" className="mt-16 border border-dashed border-stone-300 py-20 text-center">
          <HeartOff size={40} strokeWidth={1.2} className="mx-auto text-stone-400"/>
          <p className="mt-4 font-serif text-2xl text-stone-800">Your wishlist is empty.</p>
          <p className="mt-2 text-stone-500 text-sm">Tap the heart on any piece to save it for later.</p>
          <Link to="/" className="btn-primary inline-flex items-center gap-2 mt-8">
            Browse the collection
          </Link>
        </div>
      ) : (
        <div data-testid="wishlist-grid" className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {items.map((p) => {
            const oos = (p.stock || 0) <= 0;
            return (
              <div key={p.id} data-testid={`wishlist-item-${p.slug}`} className="group">
                <Link to={`/product/${p.slug}`} className="block relative overflow-hidden bg-stone-100 aspect-[4/5]">
                  <img src={p.images?.[0]} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
                  {oos && (
                    <span className="absolute top-3 left-3 px-2.5 py-1 text-[10px] tracking-[0.16em] uppercase bg-stone-800 text-white">
                      Out of Stock
                    </span>
                  )}
                </Link>
                <div className="mt-4">
                  <span className="text-[10px] tracking-[0.24em] uppercase text-[#8B5A2B]">{p.subcategory || p.category}</span>
                  <Link to={`/product/${p.slug}`} className="block font-serif text-xl mt-1 text-stone-900 hover:text-[#8B5A2B]">{p.name}</Link>
                  <p className="mt-1 text-sm text-stone-600">{inr(p.price)}</p>
                  <div className="mt-4 flex items-center gap-2">
                    <button
                      data-testid={`wishlist-move-to-cart-${p.slug}`}
                      onClick={() => moveToCart(p)}
                      disabled={oos}
                      className={`btn-primary flex items-center gap-2 text-xs ${oos ? "opacity-40 cursor-not-allowed" : ""}`}
                    >
                      <ShoppingBag size={13}/> {oos ? "Unavailable" : "Add to Bag"}
                    </button>
                    <button
                      data-testid={`wishlist-remove-${p.slug}`}
                      onClick={() => remove(p.id)}
                      aria-label="Remove from wishlist"
                      className="w-10 h-10 border border-stone-300 text-stone-500 hover:border-[#B23A3A] hover:text-[#B23A3A] flex items-center justify-center"
                    >
                      <Trash2 size={14}/>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
